import React, { useState, useEffect, useCallback } from 'react';

const API = window.location.origin;

const KIND_META = {
  placement: { label: 'ORDER PLACED', color: '#34d399' },
  placement_fail: { label: 'ORDER REJECTED', color: '#f87171' },
  risk_close: { label: 'RISK CLOSE', color: '#f87171' },
  cancel_stale: { label: 'CANCEL STALE', color: '#9ca3af' },
  cancel_dup: { label: 'CANCEL DUP', color: '#9ca3af' },
  backoff_skip: { label: 'BACKOFF SKIP', color: '#fbbf24' },
  equity_rebaseline: { label: 'EQUITY REBASELINE', color: '#60a5fa' },
  equity_rebaseline_discarded: { label: 'BASELINE DISCARDED', color: '#60a5fa' },
  foreign_reclaim: { label: 'RECLAIMED', color: '#fb923c' },
  foreign_adopt_on_boot: { label: 'ADOPTED ON BOOT', color: '#fb923c' },
  foreign_standoff_open: { label: 'STANDOFF OPENED', color: '#f87171' },
  foreign_standoff_close: { label: 'STANDOFF CLEARED', color: '#34d399' },
  foreign_standoff_operator_cancel: { label: 'OPERATOR CANCELLED', color: '#fbbf24' },
  foreign_standoff_operator_investigate: { label: 'OPERATOR INVESTIGATE', color: '#fbbf24' },
  reclaimed_fill: { label: 'RECLAIMED FILL', color: '#fb923c' },
  // A+B (04 Aug 2026): resting CT stop lifecycle events so armed closes surface
  // in the activity feed without an operator having to grep the log manually.
  ct_stop_placed: { label: 'CT STOP PLACED', color: '#60a5fa' },
  ct_stop_fill: { label: 'CT STOP FIRED', color: '#a78bfa' },
};

const fmtDetail = (evt) => {
  const parts = [];
  if (evt.side) parts.push(evt.side.toUpperCase());
  if (evt.size) parts.push(`${evt.size}`);
  if (evt.price) parts.push(`@ $${Number(evt.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
  if (evt.baseline) parts.push(`baseline $${Number(evt.baseline).toFixed(2)}`);
  if (evt.discarded) parts.push(`discarded $${Number(evt.discarded).toFixed(2)}`);
  // A+B ct_stop fields (04 Aug 2026): stage (cut/armed), trigger, fill, realised slippage.
  if (evt.stage) parts.push(evt.stage.toUpperCase());
  if (evt.trigger) parts.push(`trig $${Number(evt.trigger).toLocaleString(undefined, { maximumFractionDigits: 5 })}`);
  if (evt.fill) parts.push(`fill $${Number(evt.fill).toLocaleString(undefined, { maximumFractionDigits: 5 })}`);
  if (evt.slippage_pct !== undefined && evt.slippage_pct !== null) {
    parts.push(`slip ${(Number(evt.slippage_pct) * 10000).toFixed(1)}bps`);
  }
  if (evt.closed_pnl !== undefined && evt.closed_pnl !== null) {
    parts.push(`pnl $${Number(evt.closed_pnl).toFixed(4)}`);
  }
  if (evt.error) parts.push(`— ${String(evt.error).slice(0, 120)}`);
  return parts.join(' ');
};

const fmtTime = (ts) => {
  const d = new Date(ts * 1000);
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
};

export const BotActivityPanel = () => {
  const [events, setEvents] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/grid-bot/event-log`, { headers });
      if (!res.ok) return;
      const json = await res.json();
      setEvents(Array.isArray(json.events) ? json.events : []);
    } catch { /* keep last value */ }
  }, []);

  useEffect(() => {
    fetchEvents();
    const id = setInterval(fetchEvents, 30000);
    return () => clearInterval(id);
  }, [fetchEvents]);

  const recent = (events || []).slice(-30).reverse();

  return (
    <div data-testid="bot-activity-panel" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#9ca3af' }}>Bot Activity</span>
        <button
          type="button"
          data-testid="bot-activity-toggle"
          onClick={() => setExpanded((v) => !v)}
          style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 11, padding: 0 }}
        >
          {expanded ? '▾' : '▸'}
        </button>
        {events && events.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
            last {Math.min(events.length, 30)} of {events.length}
          </span>
        )}
      </div>
      {expanded && (
        !events ? (
          <div style={{ fontSize: 12, color: '#6b7280' }}>Loading…</div>
        ) : recent.length === 0 ? (
          <div data-testid="bot-activity-empty" style={{ fontSize: 12, color: '#6b7280', background: '#111827', borderRadius: 6, padding: '8px 10px' }}>
            No activity recorded yet — events appear here once the bot places, cancels, or closes orders.
          </div>
        ) : (
          <div style={{ background: '#111827', borderRadius: 6, padding: '4px 0', maxHeight: 260, overflowY: 'auto' }}>
            {recent.map((evt, i) => {
              const meta = KIND_META[evt.kind] || { label: (evt.kind || 'EVENT').toUpperCase().replace(/_/g, ' '), color: '#9ca3af' };
              return (
                <div
                  key={`${evt.ts}-${i}`}
                  data-testid={`bot-activity-row-${i}`}
                  style={{
                    display: 'flex', alignItems: 'baseline', gap: 8,
                    padding: '5px 12px', fontSize: 12,
                    borderBottom: i < recent.length - 1 ? '1px solid #1f2937' : 'none',
                  }}
                >
                  <span style={{ color: '#6b7280', fontSize: 11, whiteSpace: 'nowrap' }}>{fmtTime(evt.ts)}</span>
                  <span style={{ color: meta.color, fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{meta.label}</span>
                  <span style={{ color: '#d1d5db', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {fmtDetail(evt)}
                  </span>
                  {Number(evt.count || 1) > 1 && (
                    <span
                      data-testid={`bot-activity-count-${i}`}
                      style={{ background: '#374151', color: '#e5e7eb', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}
                    >
                      ×{evt.count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default BotActivityPanel;
